import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { FaBold, FaItalic, FaHeading, FaListUl, FaListOl, FaExpand, FaStrikethrough, FaTextHeight, FaAlignLeft, FaAlignCenter, FaAlignRight, FaParagraph, FaIndent, FaOutdent, FaAt, FaCodeBranch, FaAngleDoubleDown, FaAngleDoubleUp, FaUndo, FaRedo } from 'react-icons/fa';
import { Mark, Extension, Node } from '@tiptap/core';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import InternalLinkPickerModal from './InternalLinkPickerModal';
import {
  ancestorIdsFromAttr,
  getCachedEmbeddedNoteBodyHtml,
  getEmbeddedNoteBodyVersion,
  getInternalLinkElementFromEventTarget,
  getInternalLinkPayloadFromElement,
  normalizeInternalLinkMarkup,
} from '../utils/internalNoteLinks';

// Keep these references stable so effect deps don't retrigger endlessly on default props.
// A fresh {} / [] default here can cause editor transaction loops and route-transition stalls.
const EMPTY_LINK_TARGETS = Object.freeze([]);
const EMPTY_NOTES_BY_ID = Object.freeze({});

const btnStyle = {
  background: 'transparent',
  backgroundColor: 'transparent',
  appearance: 'none',
  WebkitAppearance: 'none',
  WebkitTapHighlightColor: 'transparent',
  border: 'none',
  borderBottom: 'none',
  color: 'var(--text-primary)',
  textDecoration: 'none',
  padding: '5px 8px',
  cursor: 'pointer',
  borderRadius: '4px',
  boxShadow: 'none',
  outline: 'none',
  opacity: 1,
  filter: 'none',
  fontSize: '0.88rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '28px',
  minHeight: '28px',
};

const activeBtnStyle = {
  ...btnStyle,
  backgroundColor: 'transparent',
  color: 'var(--text-primary)',
  border: '1px solid var(--text-primary)',
  opacity: 1,
  filter: 'none',
};

const FontSize = Mark.create({
  name: 'fontSize',
  priority: 1000,

  addAttributes() {
    return {
      size: {
        default: null,
        renderHTML: (attributes) => {
          if (!attributes.size) return {};
          return { style: `font-size: ${attributes.size}` };
        },
      },
    };
  },

  parseHTML() {
    return [{
      tag: 'span',
      getAttrs: (element) => {
        const fontSize = element.style?.fontSize;
        return fontSize ? { size: fontSize } : false;
      },
    }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', HTMLAttributes, 0];
  },

  addCommands() {
    return {
      setFontSize:
        (size) =>
        ({ commands }) =>
          commands.setMark(this.name, { size }),
      unsetFontSize:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },
});

const LineHeight = Extension.create({
  name: 'lineHeight',

  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading', 'listItem'],
        attributes: {
          lineHeight: {
            default: null,
            parseHTML: (element) => element.style.lineHeight || null,
            renderHTML: (attributes) => {
              if (!attributes.lineHeight) return {};
              return { style: `line-height: ${attributes.lineHeight}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setLineHeight:
        (lineHeight) =>
        ({ commands }) => {
          const p = commands.updateAttributes('paragraph', { lineHeight });
          const h = commands.updateAttributes('heading', { lineHeight });
          const l = commands.updateAttributes('listItem', { lineHeight });
          return p || h || l;
        },
    };
  },
});

const ParagraphLayout = Extension.create({
  name: 'paragraphLayout',

  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading', 'listItem'],
        attributes: {
          textAlign: {
            default: null,
            parseHTML: (element) => element.style.textAlign || null,
            renderHTML: (attributes) => {
              if (!attributes.textAlign) return {};
              return { style: `text-align: ${attributes.textAlign}` };
            },
          },
          dir: {
            default: null,
            parseHTML: (element) => element.getAttribute('dir') || null,
            renderHTML: (attributes) => {
              if (!attributes.dir) return {};
              return { dir: attributes.dir };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setParagraphAlign:
        (textAlign) =>
        ({ commands }) => {
          const p = commands.updateAttributes('paragraph', { textAlign });
          const h = commands.updateAttributes('heading', { textAlign });
          const l = commands.updateAttributes('listItem', { textAlign });
          return p || h || l;
        },
      setParagraphDirection:
        (dir) =>
        ({ commands }) => {
          const p = commands.updateAttributes('paragraph', { dir });
          const h = commands.updateAttributes('heading', { dir });
          const l = commands.updateAttributes('listItem', { dir });
          return p || h || l;
        },
    };
  },
});

const FontFamily = Mark.create({
  name: 'fontFamily',
  priority: 1000,

  addAttributes() {
    return {
      family: {
        default: null,
        renderHTML: (attributes) => {
          if (!attributes.family) return {};
          return { style: `font-family: ${attributes.family}` };
        },
      },
    };
  },

  parseHTML() {
    return [{
      tag: 'span',
      getAttrs: (element) => {
        const fontFamily = element.style?.fontFamily;
        return fontFamily ? { family: fontFamily } : false;
      },
    }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', HTMLAttributes, 0];
  },

  addCommands() {
    return {
      setFontFamily:
        (family) =>
        ({ commands }) =>
          commands.setMark(this.name, { family }),
      unsetFontFamily:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },
});

function InternalLinkNodeView(props) {
  const { node, extension, deleteNode, editor, getPos } = props;
  const targetType = String(node?.attrs?.targetType || 'note');
  const targetId = String(node?.attrs?.targetId || '').trim();
  const fallbackTitle = String(node?.attrs?.title || '').trim() || 'Untitled Note';
  const getNoteById = extension?.options?.getNoteById;
  const getNotesById = extension?.options?.getNotesById;
  const onNavigate = extension?.options?.onNavigate;
  const scope = String(extension?.options?.collapseStateScope || 'notes');
  const notesById = (typeof getNotesById === 'function' ? getNotesById() : {}) || {};
  const source = targetType === 'note' && typeof getNoteById === 'function'
    ? getNoteById(targetId)
    : null;
  const title = String(source?.title || fallbackTitle).trim() || 'Untitled Note';
  const canExpand = targetType === 'note' && Boolean(targetId);
  const storageKey = `internal-link-card-open:${scope}:${targetId}`;
  const [expanded, setExpanded] = useState(() => {
    if (!canExpand) return false;
    try {
      return localStorage.getItem(storageKey) === '1';
    } catch (_err) {
      return false;
    }
  });

  useEffect(() => {
    if (!canExpand) return;
    try {
      setExpanded(localStorage.getItem(storageKey) === '1');
    } catch (_err) {
      setExpanded(false);
    }
  }, [storageKey, canExpand, targetId]);

  useEffect(() => {
    if (!canExpand) return;
    try {
      localStorage.setItem(storageKey, expanded ? '1' : '0');
    } catch (_err) {
      // Ignore storage failures.
    }
  }, [expanded, storageKey, canExpand]);

  const navigate = (event, payload = null) => {
    event.preventDefault();
    event.stopPropagation();
    const nextPayload = payload || {
      targetType,
      targetId,
      title,
      href: targetType === 'folder' ? `/sets/${targetId}` : `/tune/${targetId}`,
    };
    onNavigate?.(nextPayload);
  };

  const handleNestedBodyClick = (event) => {
    const card = event?.target?.closest?.('details.internal-note-link-card');
    if (card) {
      const clickedTitle = Boolean(event?.target?.closest?.('.internal-note-link-card-title-link'));
      if (!clickedTitle) {
        // Let details arrow/summary toggle without navigating.
        event.stopPropagation();
        return;
      }
    }
    const payload = getInternalLinkPayloadFromElement(getInternalLinkElementFromEventTarget(event?.target));
    if (!payload) return;
    navigate(event, payload);
  };

  const moveCursorAroundNode = (direction = 'before', event = null) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!editor || typeof getPos !== 'function') return;
    const pos = getPos();
    if (!Number.isFinite(pos)) return;
    const targetPos = direction === 'after' ? pos + node.nodeSize : pos;
    const resolved = editor.state.doc.resolve(Math.max(0, targetPos));
    const selection = TextSelection.near(resolved, direction === 'after' ? 1 : -1);
    editor.view.dispatch(editor.state.tr.setSelection(selection));
    editor.view.focus();
  };

  const bodyVersion = getEmbeddedNoteBodyVersion(targetId, notesById);
  const bodyHtml = expanded ? getCachedEmbeddedNoteBodyHtml(targetId, notesById) : '';
  const nestedBodyRef = React.useRef(null);

  useEffect(() => {
    if (targetType !== 'note') return undefined;
    if (!expanded) return undefined;
    const root = nestedBodyRef.current;
    if (!(root instanceof HTMLElement)) return undefined;

    const hydrateCardBody = (card) => {
      if (!(card instanceof HTMLElement)) return;
      const nestedTargetId = String(card.getAttribute('data-amn-link-id') || '').trim();
      if (!nestedTargetId || !card.open) return;
      const bodyEl = card.querySelector(':scope > .internal-note-link-card-body');
      if (!(bodyEl instanceof HTMLElement)) return;
      const ancestors = ancestorIdsFromAttr(card.getAttribute('data-amn-ancestors') || '');
      const currentNotesById = (typeof getNotesById === 'function' ? getNotesById() : {}) || {};
      const nextVersion = `${getEmbeddedNoteBodyVersion(nestedTargetId, currentNotesById)}::${ancestors.join(',')}`;
      if (bodyEl.getAttribute('data-amn-body-version') === nextVersion && bodyEl.innerHTML) return;
      bodyEl.innerHTML = getCachedEmbeddedNoteBodyHtml(nestedTargetId, currentNotesById, ancestors);
      bodyEl.setAttribute('data-amn-body-version', nextVersion);
    };

    const cards = root.querySelectorAll('details.internal-note-link-card[data-amn-link-id]');
    cards.forEach((card) => {
      const nestedTargetId = String(card.getAttribute('data-amn-link-id') || '').trim();
      if (!nestedTargetId) return;
      const key = `internal-link-card-open:${scope}:${nestedTargetId}`;
      try {
        card.open = localStorage.getItem(key) === '1';
      } catch (_err) {
        card.open = false;
      }
      if (card.open) hydrateCardBody(card);
    });

    const onToggle = (event) => {
      const card = event.target;
      if (!(card instanceof HTMLElement)) return;
      if (!card.matches('details.internal-note-link-card[data-amn-link-id]')) return;
      const nestedTargetId = String(card.getAttribute('data-amn-link-id') || '').trim();
      if (!nestedTargetId) return;
      const key = `internal-link-card-open:${scope}:${nestedTargetId}`;
      try {
        localStorage.setItem(key, card.open ? '1' : '0');
      } catch (_err) {
        // Ignore storage failures.
      }
      if (card.open) hydrateCardBody(card);
    };

    root.addEventListener('toggle', onToggle, true);
    return () => {
      root.removeEventListener('toggle', onToggle, true);
    };
  }, [expanded, bodyVersion, scope, getNotesById, targetType]);

  if (targetType !== 'note') {
    return (
      <NodeViewWrapper
        as="span"
        className="internal-note-link"
        data-amn-link="1"
        data-amn-link-type={targetType}
        data-amn-link-id={targetId}
        contentEditable={false}
        onMouseDown={navigate}
      >
        {title}
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper
      as="span"
      className={`internal-note-link internal-note-link-card internal-note-link-inline-card ${expanded ? 'is-open' : ''}`}
      data-amn-link="1"
      data-amn-link-type="note"
      data-amn-link-id={targetId}
      data-expanded={expanded ? '1' : '0'}
      contentEditable={false}
      onMouseDown={(event) => {
        if (event.target !== event.currentTarget) return;
        const rect = event.currentTarget.getBoundingClientRect();
        const direction = (event.clientY - rect.top) > (rect.height / 2) ? 'after' : 'before';
        moveCursorAroundNode(direction, event);
      }}
    >
      <span className="internal-note-link-card-title internal-note-link-inline-summary">
        <span
          className="internal-note-link-card-arrow internal-note-link-inline-toggle"
          role="button"
          tabIndex={0}
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setExpanded((prev) => !prev);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            event.stopPropagation();
            setExpanded((prev) => !prev);
          }}
        />
        <span
          className="internal-note-link-card-title-link internal-note-link-inline-title"
          role="link"
          tabIndex={0}
          onMouseDown={navigate}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            navigate(event);
          }}
        >
          {title}
        </span>
        <button
          type="button"
          className="internal-note-link-inline-remove"
          tabIndex={-1}
          aria-label="Remove embedded note reference"
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            deleteNode?.();
          }}
        >
          ×
        </button>
      </span>
      {expanded && (
        <span
          ref={nestedBodyRef}
          className="internal-note-link-card-body internal-note-link-inline-body"
          data-amn-body-version={bodyVersion}
          onMouseDown={handleNestedBodyClick}
          onClick={handleNestedBodyClick}
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />
      )}
    </NodeViewWrapper>
  );
}

const InternalLinkNode = Node.create({
  name: 'internalLink',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addOptions() {
    return {
      getNoteById: () => null,
      getNotesById: () => ({}),
      onNavigate: null,
      collapseStateScope: 'notes',
    };
  },

  addAttributes() {
    return {
      targetType: {
        default: 'note',
        parseHTML: (el) => el.getAttribute('data-amn-link-type') || 'note',
        renderHTML: (attrs) => ({ 'data-amn-link-type': attrs.targetType || 'note' }),
      },
      targetId: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-amn-link-id') || '',
        renderHTML: (attrs) => ({ 'data-amn-link-id': attrs.targetId || '' }),
      },
      title: {
        default: '',
        parseHTML: (el) => el.textContent || '',
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-amn-link="1"]' }, { tag: 'a[data-amn-link="1"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const { title, ...attrs } = HTMLAttributes;
    return [
      'span',
      {
        ...attrs,
        class: 'internal-note-link',
        role: 'link',
        tabindex: '0',
        contenteditable: 'false',
        'data-amn-link': '1',
        'data-amn-link-title': String(title || 'Untitled'),
      },
      String(title || 'Untitled'),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(InternalLinkNodeView);
  },
});

const looksLikeHtml = (value = '') => /<\/?[a-z][\w-]*(\s[^>]*)?>/i.test(String(value || ''));
const escapeHtml = (value = '') =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

function plainTextToHtml(text) {
  if (!text) return '';
  if (looksLikeHtml(text)) return normalizeInternalLinkMarkup(text);
  return String(text)
    .split('\n')
    .map((line) => `<p>${line ? escapeHtml(line) : '<br>'}</p>`)
    .join('');
}

function getForkContextFromEditor(editor, { stripSlashSuffix = false } = {}) {
  if (!editor?.state) return null;
  const { state } = editor;
  const { from: selFrom, to: selTo, empty } = state.selection;
  let replaceFrom = selFrom;
  let replaceTo = selTo;
  let titleCandidate = '';

  if (!empty) {
    titleCandidate = state.doc.textBetween(selFrom, selTo, ' ').trim();
  } else {
    const { $from } = state.selection;
    replaceFrom = $from.start();
    replaceTo = $from.end();
    titleCandidate = state.doc.textBetween(replaceFrom, replaceTo, ' ').trim();
  }

  if (stripSlashSuffix) {
    titleCandidate = titleCandidate
      .replace(/\/\/\s*$/, '')
      .replace(/\/\s*$/, '')
      .trim();
  }

  return {
    replaceFrom,
    replaceTo,
    titleCandidate,
  };
}

const NotesEditor = forwardRef(function NotesEditor({
  value,
  onChange,
  placeholder = 'Notes',
  onFullscreen,
  toolbarExtraRight = null,
  headerContent = null,
  headerInToolbarWhenMinimal = false,
  minimalToolbar = false,
  hideToolbar = false,
  enableInternalLinks = false,
  internalLinkTargets = EMPTY_LINK_TARGETS,
  internalLinkNotesById = EMPTY_NOTES_BY_ID,
  onInternalLinkNavigate = null,
  onForkNoteRequested = null,
  showForkButton = false,
}, ref) {
  const [internalLinkPickerOpen, setInternalLinkPickerOpen] = useState(false);
  const [, setToolbarVersion] = useState(0);
  const forkContextRef = React.useRef(null);
  const stableNotesById = internalLinkNotesById || EMPTY_NOTES_BY_ID;
  const stableLinkTargets = internalLinkTargets || EMPTY_LINK_TARGETS;
  const notesByIdRef = useRef(stableNotesById);
  const internalNavigateRef = useRef(onInternalLinkNavigate);
  const lineSpacings = useMemo(() => [
    { value: '0.9', label: '1' },
    { value: '1.3', label: '1.5' },
    { value: '1.7', label: '2' },
  ], []);
  const shortcutModLabel = useMemo(() => {
    if (typeof navigator === 'undefined') return 'Ctrl';
    const platform = navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || '';
    return /Mac|iPhone|iPad|iPod/i.test(platform) ? 'Cmd' : 'Ctrl';
  }, []);
  const showHeaderInToolbar = Boolean(
    !hideToolbar && minimalToolbar && headerInToolbarWhenMinimal && headerContent
  );

  useEffect(() => {
    notesByIdRef.current = stableNotesById;
  }, [stableNotesById]);

  useEffect(() => {
    internalNavigateRef.current = onInternalLinkNavigate;
  }, [onInternalLinkNavigate]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      FontSize,
      LineHeight,
      FontFamily,
      ParagraphLayout,
      InternalLinkNode.configure({
        getNoteById: (id) => (notesByIdRef.current || {})?.[String(id || '').trim()] || null,
        getNotesById: () => notesByIdRef.current || {},
        onNavigate: (payload) => internalNavigateRef.current?.(payload),
        collapseStateScope: 'notes-editor',
      }),
    ],
    content: plainTextToHtml(value),
    onUpdate: ({ editor }) => {
      // Emit '' when editor has no visible text (avoids '<p></p>' counting as content)
      const text = editor.state.doc.textContent?.trim();
      onChange(text ? normalizeInternalLinkMarkup(editor.getHTML()) : '');
    },
    editorProps: {
      handleDOMEvents: {
        mousedown: (_view, event) => {
          if (!enableInternalLinks) return false;
          const linkEl = getInternalLinkElementFromEventTarget(event?.target);
          const payload = getInternalLinkPayloadFromElement(linkEl);
          if (!payload) return false;
          event.preventDefault();
          event.stopPropagation();
          onInternalLinkNavigate?.(payload);
          return true;
        },
      },
      handleTextInput: (_view, from, _to, text) => {
        if (!enableInternalLinks || typeof onForkNoteRequested !== 'function') return false;
        if (text !== '/') return false;
        if (!editor?.state?.doc || from < 1) return false;
        const prevChar = editor.state.doc.textBetween(from - 1, from, '', '') || '';
        if (prevChar !== '/') return false;

        const nextFork = getForkContextFromEditor(editor, { stripSlashSuffix: true });
        if (!nextFork) return false;

        const tr = editor.state.tr.delete(from - 1, from);
        editor.view.dispatch(tr);
        forkContextRef.current = nextFork;
        onForkNoteRequested({
          titleCandidate: nextFork.titleCandidate,
        });
        return true;
      },
      handleKeyDown: (_view, event) => {
        const selection = editor?.state?.selection;
        if (selection instanceof NodeSelection && selection.node?.type?.name === 'internalLink') {
          if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
            event.preventDefault();
            const resolved = editor.state.doc.resolve(Math.max(0, selection.from));
            editor.view.dispatch(editor.state.tr.setSelection(TextSelection.near(resolved, -1)));
            return true;
          }
          if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
            event.preventDefault();
            const resolved = editor.state.doc.resolve(Math.max(0, selection.to));
            editor.view.dispatch(editor.state.tr.setSelection(TextSelection.near(resolved, 1)));
            return true;
          }
        }
        if (enableInternalLinks && event.key === '[' && !event.metaKey && !event.ctrlKey && !event.altKey) {
          const from = editor?.state?.selection?.from || 0;
          if (from > 1) {
            const prevChar = editor?.state?.doc?.textBetween?.(from - 1, from, '', '') || '';
            if (prevChar === '[') {
              event.preventDefault();
              editor?.chain().focus().deleteRange({ from: from - 1, to: from }).run();
              setInternalLinkPickerOpen(true);
              return true;
            }
          }
        }
        if (enableInternalLinks && typeof onForkNoteRequested === 'function' && event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey) {
          const from = editor?.state?.selection?.from || 0;
          if (from > 1) {
            const prevChar = editor?.state?.doc?.textBetween?.(from - 1, from, '', '') || '';
            if (prevChar === '/') {
              const nextFork = getForkContextFromEditor(editor, { stripSlashSuffix: true });
              if (!nextFork) return false;

              event.preventDefault();
              editor?.chain().focus().deleteRange({ from: from - 1, to: from }).run();
              forkContextRef.current = nextFork;
              onForkNoteRequested({
                titleCandidate: nextFork.titleCandidate,
              });
              return true;
            }
          }
        }
        if (event.key !== 'Tab') return false;
        event.preventDefault();
        const spaces = '     '; // 5 spaces
        editor?.chain().focus().insertContent(spaces).run();
        return true;
      },
      handleClick: (_view, _pos, event) => {
        if (!enableInternalLinks) return false;
        const linkEl = getInternalLinkElementFromEventTarget(event?.target);
        const payload = getInternalLinkPayloadFromElement(linkEl);
        if (!payload) return false;
        event.preventDefault();
        if (typeof onInternalLinkNavigate === 'function') {
          onInternalLinkNavigate(payload);
        }
        return true;
      },
      attributes: {
        class: 'notes-tiptap-editor',
        'data-placeholder': placeholder,
      },
    },
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    // Trigger a lightweight re-render for node views when linked note data changes.
    try {
      const tr = editor.state.tr.setMeta('internalLinkNotesRefresh', Date.now());
      editor.view.dispatch(tr);
    } catch (_err) {
      // Ignore refresh failures (editor view may not be mounted yet).
    }
  }, [editor, stableNotesById]);

  useEffect(() => {
    if (!editor) return;
    const refreshToolbar = () => setToolbarVersion((v) => v + 1);
    editor.on('transaction', refreshToolbar);
    editor.on('selectionUpdate', refreshToolbar);
    return () => {
      editor.off('transaction', refreshToolbar);
      editor.off('selectionUpdate', refreshToolbar);
    };
  }, [editor]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return undefined;
    let root;
    try { root = editor.view.dom; } catch (_err) { return undefined; }
    if (!root) return undefined;
    const onToggle = (event) => {
      const card = event.target;
      if (!(card instanceof HTMLElement)) return;
      if (!card.matches('details.internal-note-link-card[data-amn-link-id]')) return;
      setToolbarVersion((v) => v + 1);
    };
    root.addEventListener('toggle', onToggle, true);
    return () => {
      root.removeEventListener('toggle', onToggle, true);
    };
  }, [editor]);

  // Sync external value changes (e.g. loading saved data)
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    try {
      const current = editor.getHTML();
      const incoming = plainTextToHtml(value);
      const currentNormalized = normalizeInternalLinkMarkup(current);
      const incomingNormalized = normalizeInternalLinkMarkup(incoming);
      if (incomingNormalized !== currentNormalized) {
        editor.commands.setContent(incoming, false);
      }
    } catch (_err) {
      // Editor view not yet mounted — skip sync for this render cycle.
    }
  }, [value, editor]);

  useImperativeHandle(ref, () => ({
    getHtml: () => normalizeInternalLinkMarkup(editor.getHTML()),
    focusEditor: () => {
      if (!editor) return;
      editor.chain().focus().run();
    },
    insertInternalLinkTarget: (target) => {
      if (!editor) return null;
      const targetId = String(target?.id || '').trim();
      if (!targetId) return null;
      const ctx = forkContextRef.current;
      let chain = editor.chain().focus();
      if (ctx && Number.isFinite(ctx.replaceFrom) && Number.isFinite(ctx.replaceTo) && ctx.replaceTo >= ctx.replaceFrom) {
        chain = chain.deleteRange({ from: ctx.replaceFrom, to: ctx.replaceTo });
      }
      chain
        .insertContent({
          type: 'internalLink',
          attrs: {
            targetType: target?.targetType || 'note',
            targetId,
            title: String(target?.title || '').trim() || 'Untitled',
          },
        })
        .insertContent(' ')
        .run();
      forkContextRef.current = null;
      return normalizeInternalLinkMarkup(editor.getHTML());
    },
    clearForkContext: () => {
      forkContextRef.current = null;
    },
  }), [editor]);

  const currentFontSizePx = Number.parseInt((editor?.getAttributes('fontSize')?.size || '16px').replace('px', ''), 10) || 16;

  if (!editor) return null;

  const runEditorCommand = (applyFn) => applyFn(editor.chain().focus()).run();
  const openInternalLinkPicker = () => setInternalLinkPickerOpen((prev) => !prev);
  const closeInternalLinkPicker = () => setInternalLinkPickerOpen(false);
  const requestForkNoteFromEditor = () => {
    if (!enableInternalLinks || typeof onForkNoteRequested !== 'function') return;
    const nextFork = getForkContextFromEditor(editor, { stripSlashSuffix: false });
    if (!nextFork) return;
    forkContextRef.current = nextFork;
    onForkNoteRequested({ titleCandidate: nextFork.titleCandidate });
  };
  const insertInternalLink = (target) => {
    const targetId = String(target?.id || '').trim();
    if (!targetId) return;
    if (forkContextRef.current) {
      // Fork command owns this slot until modal confirms/cancels.
      forkContextRef.current = null;
    }
    editor.chain().focus().insertContent({
      type: 'internalLink',
      attrs: {
        targetType: target?.targetType || 'note',
        targetId,
        title: String(target?.title || '').trim() || 'Untitled',
      },
    }).insertContent(' ').run();
    closeInternalLinkPicker();
  };

  const stepFont = (dir) => {
    const next = Math.max(8, Math.min(72, currentFontSizePx + dir));
    runEditorCommand((chain) => chain.setFontSize(`${next}px`));
  };

  const tools = [
    { icon: FaUndo, label: 'Undo', action: () => runEditorCommand((chain) => chain.undo()), active: false, disabled: !editor.can().undo(), compact: true },
    { icon: FaRedo, label: 'Redo', action: () => runEditorCommand((chain) => chain.redo()), active: false, disabled: !editor.can().redo(), compact: true },
    { icon: FaHeading, label: 'Heading', action: () => runEditorCommand((chain) => chain.toggleHeading({ level: 2 })), active: editor.isActive('heading', { level: 2 }) },
    { icon: FaBold, label: 'Bold', action: () => runEditorCommand((chain) => chain.toggleBold()), active: editor.isActive('bold') },
    { icon: FaItalic, label: 'Italic', action: () => runEditorCommand((chain) => chain.toggleItalic()), active: editor.isActive('italic') },
    { icon: FaStrikethrough, label: 'Strikethrough', action: () => runEditorCommand((chain) => chain.toggleStrike()), active: editor.isActive('strike') },
    { icon: FaListUl, label: 'Bullet list', action: () => runEditorCommand((chain) => chain.toggleBulletList()), active: editor.isActive('bulletList') },
    { icon: FaListOl, label: 'Numbered list', action: () => runEditorCommand((chain) => chain.toggleOrderedList()), active: editor.isActive('orderedList') },
    {
      icon: FaOutdent,
      label: 'Outdent',
      action: () => runEditorCommand((chain) => chain.liftListItem('listItem')),
      active: false,
      compact: true,
    },
    {
      icon: FaIndent,
      label: 'Indent',
      action: () => runEditorCommand((chain) => chain.sinkListItem('listItem')),
      active: false,
      compact: true,
    },
  ];
  const currentParagraph = editor.getAttributes('paragraph');
  const activeAlign = currentParagraph?.textAlign || 'left';
  const activeDir = currentParagraph?.dir || 'ltr';
  const activeLineHeight =
    currentParagraph?.lineHeight ||
    editor.getAttributes('heading')?.lineHeight ||
    editor.getAttributes('listItem')?.lineHeight ||
    null;

  const fontFamilies = [
    { key: 'sans', label: 'Sans', short: 'ss', family: null },
    { key: 'serif', label: 'Serif', short: 's', family: '"Times New Roman", Times, serif' },
    { key: 'mono', label: 'Mono', short: 'm', family: '"Courier New", Courier, monospace' },
  ];

  const activeFontFamily = editor.getAttributes('fontFamily')?.family || null;
  const cycleLineSpacing = () => {
    const currentIndex = lineSpacings.findIndex((opt) => opt.value === activeLineHeight);
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % lineSpacings.length : 0;
    runEditorCommand((chain) => chain.setLineHeight(lineSpacings[nextIndex].value));
  };
  const cycleFontFamily = () => {
    const currentIndex = fontFamilies.findIndex((ff) => (ff.family || null) === activeFontFamily);
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % fontFamilies.length : 0;
    const nextFamily = fontFamilies[nextIndex]?.family || null;
    if (!nextFamily) {
      runEditorCommand((chain) => chain.unsetFontFamily());
      return;
    }
    runEditorCommand((chain) => chain.setFontFamily(nextFamily));
  };
  const cycleAlignment = () => {
    const alignments = ['left', 'center', 'right'];
    const currentIndex = alignments.indexOf(activeAlign);
    const nextAlign = alignments[(currentIndex + 1 + alignments.length) % alignments.length];
    runEditorCommand((chain) => chain.setParagraphAlign(nextAlign));
  };
  const toggleParagraphDirection = () => {
    runEditorCommand((chain) => chain.setParagraphDirection(activeDir === 'rtl' ? 'ltr' : 'rtl'));
  };

  const normalizeButtonUi = (target) => {
    // Prevent stuck focus/pressed tint overlays on some browsers.
    if (target && typeof target.blur === 'function') target.blur();
  };

  const hydrateEditorCardBody = (card, notesMap) => {
    if (!(card instanceof HTMLElement) || !card.open) return;
    const targetId = String(card.getAttribute('data-amn-link-id') || '').trim();
    if (!targetId) return;
    const bodyEl = card.querySelector(':scope > .internal-note-link-card-body');
    if (!(bodyEl instanceof HTMLElement)) return;
    const ancestors = ancestorIdsFromAttr(card.getAttribute('data-amn-ancestors') || '');
    const version = `${getEmbeddedNoteBodyVersion(targetId, notesMap)}::${ancestors.join(',')}`;
    if (bodyEl.getAttribute('data-amn-body-version') === version && bodyEl.innerHTML) return;
    bodyEl.innerHTML = getCachedEmbeddedNoteBodyHtml(targetId, notesMap, ancestors);
    bodyEl.setAttribute('data-amn-body-version', version);
  };

  const topLevelInlineCards = Array.from(
    editor.view.dom.querySelectorAll('span.internal-note-link-inline-card[data-amn-link-id]')
  );
  const nestedDetailCards = Array.from(
    editor.view.dom.querySelectorAll('details.internal-note-link-card[data-amn-link-id]')
  );
  const hasEmbeddedCards = topLevelInlineCards.length > 0 || nestedDetailCards.length > 0;
  const topLevelAllOpen = topLevelInlineCards.every((card) => card.getAttribute('data-expanded') === '1' || card.classList.contains('is-open'));
  const nestedAllOpen = nestedDetailCards.every((card) => card.open);
  const allEmbeddedCardsOpen = hasEmbeddedCards && topLevelAllOpen && nestedAllOpen;

  const toggleAllEmbeddedCards = (expand = true) => {
    const root = editor?.view?.dom;
    if (!(root instanceof HTMLElement)) return;
    const notesMap = (notesByIdRef.current || {});
    const maxPasses = 24;
    const setTopLevelExpanded = (nextExpanded) => {
      const cards = Array.from(root.querySelectorAll('span.internal-note-link-inline-card[data-amn-link-id]'));
      cards.forEach((card) => {
        const isOpen = card.getAttribute('data-expanded') === '1' || card.classList.contains('is-open');
        if (isOpen === nextExpanded) return;
        const toggleEl = card.querySelector(':scope > .internal-note-link-inline-summary .internal-note-link-inline-toggle');
        if (!(toggleEl instanceof HTMLElement)) return;
        try {
          toggleEl.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true, view: window }));
        } catch (_err) {
          // Ignore synthetic event failures.
        }
      });
    };

    if (!expand) {
      setTopLevelExpanded(false);
      root.querySelectorAll('details.internal-note-link-card[data-amn-link-id]').forEach((card) => {
        const targetId = String(card.getAttribute('data-amn-link-id') || '').trim();
        if (targetId) {
          try {
            localStorage.setItem(`internal-link-card-open:notes-editor:${targetId}`, '0');
          } catch (_err) {
            // Ignore storage failures.
          }
        }
        card.open = false;
      });
      setToolbarVersion((v) => v + 1);
      return;
    }

    setTopLevelExpanded(true);
    const expandNested = (pass = 0) => {
      if (pass >= maxPasses) {
        setToolbarVersion((v) => v + 1);
        return;
      }
      const cards = Array.from(root.querySelectorAll('details.internal-note-link-card[data-amn-link-id]'));
      let changed = false;
      cards.forEach((card) => {
        const targetId = String(card.getAttribute('data-amn-link-id') || '').trim();
        if (!targetId) return;
        if (!card.open) {
          card.open = true;
          changed = true;
        }
        try {
          localStorage.setItem(`internal-link-card-open:notes-editor:${targetId}`, '1');
        } catch (_err) {
          // Ignore storage failures.
        }
        hydrateEditorCardBody(card, notesMap);
      });
      if (!changed) {
        setToolbarVersion((v) => v + 1);
        return;
      }
      window.setTimeout(() => expandNested(pass + 1), 0);
    };
    expandNested(0);
  };

  return (
    <div className={`notes-editor-wrap ${hideToolbar ? 'notes-editor-no-toolbar' : ''}`}>
      {!hideToolbar && (
      <div style={{
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: '2px',
        padding: minimalToolbar ? '3px 5px 4px' : '3px 5px',
        backgroundColor: 'var(--notes-toolbar-bg, var(--input-bg))',
        borderRadius: '8px 8px 0 0',
        border: '1px solid color-mix(in srgb, var(--input-border) 50%, transparent)',
        borderBottom: 'none',
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
      }}
      data-swipe-nav-lock="1"
      >
        {showHeaderInToolbar ? (
          <div style={{ flex: 1, minWidth: 0, padding: '0 6px', display: 'flex', alignItems: 'center' }}>
            {headerContent}
          </div>
        ) : null}
        {!minimalToolbar && tools.map((item) => (
          <button
            key={item.label}
            type="button"
            title={item.label}
            disabled={item.disabled}
            onMouseDown={(e) => { e.preventDefault(); if (!item.disabled) item.action(); }}
            onMouseUp={(e) => normalizeButtonUi(e.currentTarget)}
            onTouchEnd={(e) => normalizeButtonUi(e.currentTarget)}
            style={
              item.compact
                ? {
                    ...(item.active ? activeBtnStyle : btnStyle),
                    padding: '4px 6px',
                    minWidth: '24px',
                    opacity: item.disabled ? 0.35 : 1,
                  }
                : { ...(item.active ? activeBtnStyle : btnStyle), opacity: item.disabled ? 0.35 : 1 }
            }
          >
            <item.icon />
          </button>
        ))}
        {!minimalToolbar && (
        <div style={{ marginLeft: '3px', display: 'flex', alignItems: 'center', gap: '3px', flexWrap: 'nowrap', position: 'relative' }}>
          <button
            type="button"
            title="Font family"
            onMouseDown={(e) => { e.preventDefault(); cycleFontFamily(); }}
            onMouseUp={(e) => normalizeButtonUi(e.currentTarget)}
            onTouchEnd={(e) => normalizeButtonUi(e.currentTarget)}
            style={btnStyle}
          >
            F
            <span style={{ marginLeft: '4px', fontSize: '0.68rem' }}>
              {fontFamilies.find((ff) => (ff.family || null) === activeFontFamily)?.short || 'ss'}
            </span>
          </button>

          <button
            type="button"
            title="Alignment"
            onMouseDown={(e) => { e.preventDefault(); cycleAlignment(); }}
            onMouseUp={(e) => normalizeButtonUi(e.currentTarget)}
            onTouchEnd={(e) => normalizeButtonUi(e.currentTarget)}
            style={btnStyle}
          >
            {activeAlign === 'center' ? <FaAlignCenter /> : activeAlign === 'right' ? <FaAlignRight /> : <FaAlignLeft />}
          </button>

          <button
            type="button"
            title="Paragraph direction"
            onMouseDown={(e) => { e.preventDefault(); toggleParagraphDirection(); }}
            onMouseUp={(e) => normalizeButtonUi(e.currentTarget)}
            onTouchEnd={(e) => normalizeButtonUi(e.currentTarget)}
            style={btnStyle}
          >
            <FaParagraph />
            <span style={{ marginLeft: '4px', fontSize: '0.68rem' }}>{activeDir === 'rtl' ? 'R' : 'L'}</span>
          </button>
        </div>
        )}
        {!minimalToolbar && hasEmbeddedCards && (
          <button
            type="button"
            title={allEmbeddedCardsOpen ? 'Collapse all embedded notes' : 'Expand all embedded notes'}
            onMouseDown={(e) => { e.preventDefault(); toggleAllEmbeddedCards(!allEmbeddedCardsOpen); }}
            onMouseUp={(e) => normalizeButtonUi(e.currentTarget)}
            onTouchEnd={(e) => normalizeButtonUi(e.currentTarget)}
            style={btnStyle}
          >
            {allEmbeddedCardsOpen ? <FaAngleDoubleUp /> : <FaAngleDoubleDown />}
          </button>
        )}
        {!minimalToolbar && enableInternalLinks && (
          <button
            type="button"
            title="Insert internal link ([[)"
            onMouseDown={(e) => { e.preventDefault(); openInternalLinkPicker(); }}
            onMouseUp={(e) => normalizeButtonUi(e.currentTarget)}
            onTouchEnd={(e) => normalizeButtonUi(e.currentTarget)}
            style={btnStyle}
          >
            <FaAt />
          </button>
        )}
        {!minimalToolbar && enableInternalLinks && showForkButton && typeof onForkNoteRequested === 'function' && (
          <button
            type="button"
            title="Create linked note (//)"
            onMouseDown={(e) => { e.preventDefault(); requestForkNoteFromEditor(); }}
            onMouseUp={(e) => normalizeButtonUi(e.currentTarget)}
            onTouchEnd={(e) => normalizeButtonUi(e.currentTarget)}
            style={btnStyle}
          >
            <FaCodeBranch />
          </button>
        )}
        {!minimalToolbar && (onFullscreen || toolbarExtraRight) ? (
          <div
            style={{
              position: 'sticky',
              right: '-5px',
              marginLeft: 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              paddingLeft: '10px',
              paddingRight: '5px',
              backgroundColor: 'var(--notes-toolbar-bg, var(--input-bg))',
              flexShrink: 0,
              zIndex: 3,
            }}
          >
            {onFullscreen ? (
              <button
                type="button"
                title={`Fullscreen (${shortcutModLabel}+Enter/Esc cycles modes)`}
                onMouseDown={(e) => { e.preventDefault(); onFullscreen(); }}
                onMouseUp={(e) => normalizeButtonUi(e.currentTarget)}
                onTouchEnd={(e) => normalizeButtonUi(e.currentTarget)}
                style={btnStyle}
              >
                <FaExpand />
              </button>
            ) : null}
            {toolbarExtraRight}
          </div>
        ) : null}
      </div>
      )}
      {headerContent && !showHeaderInToolbar ? (
        <div className="notes-editor-header-slot">
          {headerContent}
        </div>
      ) : null}
      <EditorContent editor={editor} />
      <InternalLinkPickerModal
        open={internalLinkPickerOpen}
        targets={stableLinkTargets}
        title="Link to note or folder"
        onClose={closeInternalLinkPicker}
        onSelect={insertInternalLink}
      />
    </div>
  );
});

export default NotesEditor;
